/**
 * Widget Settings Page Component
 * Provides interface for creating and managing chatbot widget configurations
 */
import React, { useState, useEffect } from 'react';
import { 
    PlusIcon, 
    CogIcon, 
    TrashIcon, 
    EyeIcon, 
    ClipboardDocumentIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import widgetService from '../services/widgetService';

const WidgetSettingsPage = () => {
    const [widgets, setWidgets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showScriptModal, setShowScriptModal] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [generatedScript, setGeneratedScript] = useState('');
    const [notification, setNotification] = useState(null);

    // Form state for widget configuration
    const [formData, setFormData] = useState({
        company_name: '',
        widget_name: 'My Chatbot Widget',
        theme: 'auto',
        primary_color: '#667eea',
        position: 'bottom-right',
        widget_size: 'medium',
        welcome_message: 'Hello! How can I help you today?',
        bot_name: 'Assistant',
        bot_avatar_url: '',
        company_logo_url: '',
        auto_open: false,
        show_typing_indicator: true,
        enable_file_upload: false,
        enable_voice_input: false,
        require_user_info: true,
        rate_limit_per_minute: 10,
        allowed_domains: [],
        custom_css: '',
        custom_js: ''
    });

    const [domainInput, setDomainInput] = useState('');

    useEffect(() => {
        loadUserWidgets();
    }, []);

    const loadUserWidgets = async () => {
        try {
            setLoading(true);
            const userWidgets = await widgetService.getUserWidgets();
            setWidgets(userWidgets);
            setError(null);
        } catch (err) {
            setError('Failed to load widget configurations');
            console.error('Error loading widgets:', err);
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    const resetForm = () => {
        setFormData({
            company_name: '',
            widget_name: 'My Chatbot Widget',
            theme: 'auto',
            primary_color: '#667eea',
            position: 'bottom-right',
            widget_size: 'medium',
            welcome_message: 'Hello! How can I help you today?',
            bot_name: 'Assistant',
            bot_avatar_url: '',
            company_logo_url: '',
            auto_open: false,
            show_typing_indicator: true,
            enable_file_upload: false,
            enable_voice_input: false,
            require_user_info: true,
            rate_limit_per_minute: 10,
            allowed_domains: [],
            custom_css: '',
            custom_js: ''
        });
        setDomainInput('');
    };

    const handleCreateWidget = async (e) => {
        e.preventDefault();
        try {
            await widgetService.createWidget(formData);
            showNotification('Widget created successfully!');
            setShowCreateModal(false);
            resetForm();
            loadUserWidgets();
        } catch (err) {
            showNotification('Failed to create widget', 'error');
            console.error('Error creating widget:', err);
        }
    };

    const handleUpdateWidget = async (e) => {
        e.preventDefault();
        try {
            await widgetService.updateWidget(selectedWidget.chatbot_id, formData);
            showNotification('Widget updated successfully!');
            setShowEditModal(false);
            resetForm();
            setSelectedWidget(null);
            loadUserWidgets();
        } catch (err) {
            showNotification('Failed to update widget', 'error');
            console.error('Error updating widget:', err);
        }
    };

    const handleDeleteWidget = async (chatbotId) => {
        if (!window.confirm('Are you sure you want to delete this widget? This action cannot be undone.')) {
            return;
        }

        try {
            await widgetService.deleteWidget(chatbotId);
            showNotification('Widget deleted successfully!');
            loadUserWidgets();
        } catch (err) {
            showNotification('Failed to delete widget', 'error');
            console.error('Error deleting widget:', err);
        }
    };

    const handleEditWidget = async (widget) => {
        try {
            const fullWidget = await widgetService.getWidget(widget.chatbot_id);
            setSelectedWidget(fullWidget);
            setFormData({
                company_name: fullWidget.company_name,
                widget_name: fullWidget.widget_name,
                theme: fullWidget.theme,
                primary_color: fullWidget.primary_color,
                position: fullWidget.position,
                widget_size: fullWidget.widget_size,
                welcome_message: fullWidget.welcome_message,
                bot_name: fullWidget.bot_name,
                bot_avatar_url: fullWidget.bot_avatar_url || '',
                company_logo_url: fullWidget.company_logo_url || '',
                auto_open: fullWidget.auto_open,
                show_typing_indicator: fullWidget.show_typing_indicator,
                enable_file_upload: fullWidget.enable_file_upload,
                enable_voice_input: fullWidget.enable_voice_input,
                require_user_info: fullWidget.require_user_info,
                rate_limit_per_minute: fullWidget.rate_limit_per_minute,
                allowed_domains: fullWidget.allowed_domains || [],
                custom_css: fullWidget.custom_css || '',
                custom_js: fullWidget.custom_js || ''
            });
            setShowEditModal(true);
        } catch (err) {
            showNotification('Failed to load widget details', 'error');
            console.error('Error loading widget details:', err);
        }
    };

    const handleGenerateScript = async (chatbotId) => {
        try {
            const result = await widgetService.generateScript(chatbotId);
            setGeneratedScript(result.script);
            setShowScriptModal(true);
        } catch (err) {
            showNotification('Failed to generate script', 'error');
            console.error('Error generating script:', err);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showNotification('Copied to clipboard!');
        } catch (err) {
            showNotification('Failed to copy to clipboard', 'error');
        }
    };

    const addDomain = () => {
        if (domainInput.trim() && !formData.allowed_domains.includes(domainInput.trim())) {
            setFormData({
                ...formData,
                allowed_domains: [...formData.allowed_domains, domainInput.trim()]
            });
            setDomainInput('');
        }
    };

    const removeDomain = (domain) => {
        setFormData({
            ...formData,
            allowed_domains: formData.allowed_domains.filter(d => d !== domain)
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-gray-400 mt-4">Loading widget configurations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Header */}
            <div className="bg-gray-800 border-b border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-white">Widget Settings</h1>
                                <p className="text-gray-400 mt-2">Create and manage your chatbot widget configurations</p>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Create Widget
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
                    notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
                } text-white`}>
                    <div className="flex items-center gap-2">
                        {notification.type === 'error' ? (
                            <ExclamationTriangleIcon className="h-5 w-5" />
                        ) : (
                            <CheckCircleIcon className="h-5 w-5" />
                        )}
                        {notification.message}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {error && (
                    <div className="bg-red-900/30 border border-red-800 text-red-400 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Widget List */}
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {widgets.map((widget) => (
                        <div key={widget.chatbot_id} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{widget.widget_name}</h3>
                                    <p className="text-gray-400 text-sm">{widget.company_name}</p>
                                    <p className="text-gray-500 text-xs mt-1">ID: {widget.chatbot_id}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs ${
                                    widget.is_active 
                                        ? 'bg-green-900/30 text-green-400 border border-green-800' 
                                        : 'bg-red-900/30 text-red-400 border border-red-800'
                                }`}>
                                    {widget.is_active ? 'Active' : 'Inactive'}
                                </div>
                            </div>

                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="w-4 h-4 rounded-full border-2 border-gray-600"
                                        style={{ backgroundColor: widget.primary_color }}
                                    ></div>
                                    <span className="text-sm text-gray-400">Theme: {widget.theme}</span>
                                </div>
                                <div className="text-sm text-gray-400">
                                    Position: {widget.position}
                                </div>
                                <div className="text-sm text-gray-500">
                                    Created: {new Date(widget.created_at).toLocaleDateString()}
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleEditWidget(widget)}
                                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors"
                                >
                                    <CogIcon className="h-4 w-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleGenerateScript(widget.chatbot_id)}
                                    className="flex-1 bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-1 transition-colors"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                    Script
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteWidget(widget.chatbot_id)}
                                    aria-label={`Delete widget ${widget.widget_name || ''}`.trim()}
                                    title="Delete widget"
                                    className="bg-red-700 hover:bg-red-600 text-white px-3 py-2 rounded text-sm flex items-center justify-center transition-colors motion-reduce:transition-none"
                                >
                                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    ))}

                    {widgets.length === 0 && (
                        <div className="col-span-full text-center py-12">
                            <div className="text-gray-500 mb-4">
                                <CogIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">No widget configurations found</p>
                                <p className="text-sm">Create your first chatbot widget to get started</p>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Create Your First Widget
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Widget Modal */}
            {(showCreateModal || showEditModal) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="sm-glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="p-6 border-b border-gray-700">
                            <h2 className="text-xl font-semibold text-white">
                                {showCreateModal ? 'Create New Widget' : 'Edit Widget'}
                            </h2>
                        </div>

                        <form onSubmit={showCreateModal ? handleCreateWidget : handleUpdateWidget} className="p-6 space-y-6">
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Company Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="company_name"
                                        value={formData.company_name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter company name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Widget Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="widget_name"
                                        value={formData.widget_name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter widget name"
                                    />
                                </div>
                            </div>

                            {/* Appearance Settings */}
                            <div className="border-t border-gray-700 pt-6">
                                <h3 className="text-lg font-medium text-white mb-4">Appearance</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Theme</label>
                                        <select
                                            name="theme"
                                            value={formData.theme}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="auto">Auto</option>
                                            <option value="light">Light</option>
                                            <option value="dark">Dark</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Primary Color</label>
                                        <input
                                            type="color"
                                            name="primary_color"
                                            value={formData.primary_color}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-10"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Position</label>
                                        <select
                                            name="position"
                                            value={formData.position}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="bottom-right">Bottom Right</option>
                                            <option value="bottom-left">Bottom Left</option>
                                            <option value="top-right">Top Right</option>
                                            <option value="top-left">Top Left</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Bot Configuration */}
                            <div className="border-t border-gray-700 pt-6">
                                <h3 className="text-lg font-medium text-white mb-4">Bot Configuration</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Bot Name</label>
                                        <input
                                            type="text"
                                            name="bot_name"
                                            value={formData.bot_name}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Assistant"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Widget Size</label>
                                        <select
                                            name="widget_size"
                                            value={formData.widget_size}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="small">Small</option>
                                            <option value="medium">Medium</option>
                                            <option value="large">Large</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Welcome Message</label>
                                    <textarea
                                        name="welcome_message"
                                        value={formData.welcome_message}
                                        onChange={handleInputChange}
                                        rows={3}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Hello! How can I help you today?"
                                    />
                                </div>
                            </div>

                            {/* Behavior Settings */}
                            <div className="border-t border-gray-700 pt-6">
                                <h3 className="text-lg font-medium text-white mb-4">Behavior</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="auto_open"
                                                checked={formData.auto_open}
                                                onChange={handleInputChange}
                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">Auto-open on page load</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="show_typing_indicator"
                                                checked={formData.show_typing_indicator}
                                                onChange={handleInputChange}
                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">Show typing indicator</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="require_user_info"
                                                checked={formData.require_user_info}
                                                onChange={handleInputChange}
                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">Require user registration</span>
                                        </label>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="enable_file_upload"
                                                checked={formData.enable_file_upload}
                                                onChange={handleInputChange}
                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">Enable file uploads</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="enable_voice_input"
                                                checked={formData.enable_voice_input}
                                                onChange={handleInputChange}
                                                className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">Enable voice input</span>
                                        </label>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Rate Limit (per minute)
                                            </label>
                                            <input
                                                type="number"
                                                name="rate_limit_per_minute"
                                                value={formData.rate_limit_per_minute}
                                                onChange={handleInputChange}
                                                min="1"
                                                max="100"
                                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Domain Whitelist */}
                            <div className="border-t border-gray-700 pt-6">
                                <h3 className="text-lg font-medium text-white mb-4">Allowed Domains</h3>
                                <div className="flex gap-2 mb-3">
                                    <input
                                        type="text"
                                        value={domainInput}
                                        onChange={(e) => setDomainInput(e.target.value)}
                                        placeholder="example.com"
                                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                                    />
                                    <button
                                        type="button"
                                        onClick={addDomain}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {formData.allowed_domains.map((domain) => (
                                        <span
                                            key={domain}
                                            className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                                        >
                                            {domain}
                                            <button
                                                type="button"
                                                onClick={() => removeDomain(domain)}
                                                className="text-red-400 hover:text-red-300"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="border-t border-gray-700 pt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setShowEditModal(false);
                                        resetForm();
                                        setSelectedWidget(null);
                                    }}
                                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                    {showCreateModal ? 'Create Widget' : 'Update Widget'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Script Generation Modal */}
            {showScriptModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="sm-glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="p-6 border-b border-gray-700">
                            <h2 className="text-xl font-semibold text-white">Widget Integration Script</h2>
                            <p className="text-gray-400 mt-2">Copy this script and paste it into your website's HTML</p>
                        </div>

                        <div className="p-6">
                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-4">
                                <pre className="text-sm text-gray-300 whitespace-pre-wrap overflow-x-auto">
                                    {generatedScript}
                                </pre>
                            </div>

                            <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-400">
                                    Place this script before the closing &lt;/body&gt; tag of your website
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => copyToClipboard(generatedScript)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                        Copy Script
                                    </button>
                                    <button
                                        onClick={() => setShowScriptModal(false)}
                                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WidgetSettingsPage;
